import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useForumStore, useAuthStore } from '../../stores/root_store';

export const ForumCreatePost = observer(() => {
  const { sectionSlug } = useParams<{ sectionSlug: string }>();
  const navigate = useNavigate();
  const forumStore = useForumStore();
  const authStore = useAuthStore();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sticky, setSticky] = useState(false);
  const [label, setLabel] = useState('');

  const section = sectionSlug ? forumStore.getSectionBySlug(sectionSlug) : null;
  const isManager = authStore.roles.includes('manager');

  const handleSubmit = async () => {
    if (!sectionSlug || !title.trim() || !body.trim()) return;

    const postData: {
      title: string;
      body: string;
      sticky?: boolean;
      label?: string;
    } = {
      title: title.trim(),
      body: body.trim(),
    };

    // Only include manager options if user is a manager
    if (isManager) {
      if (sticky) postData.sticky = true;
      if (label.trim()) postData.label = label.trim();
    }

    const post = await forumStore.createPost(sectionSlug, postData);
    if (post) {
      navigate(`/assistant/forum/${sectionSlug}/${post.slug}`);
    }
  };

  if (!sectionSlug) {
    return <Alert severity="error">Section not found</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Create New Post {section && `in ${section.title}`}
      </Typography>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={forumStore.saving}
              required
            />

            <TextField
              label="Content"
              fullWidth
              multiline
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={forumStore.saving}
              required
              placeholder="Write your post content here..."
            />

            {/* Manager-only options */}
            {isManager && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Manager Options
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sticky}
                      onChange={(e) => setSticky(e.target.checked)}
                      disabled={forumStore.saving}
                    />
                  }
                  label="Sticky post (pin to top)"
                />

                <TextField
                  label="Label (optional)"
                  size="small"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={forumStore.saving}
                  placeholder="e.g., Announcement, Important"
                  sx={{ maxWidth: 300 }}
                />
              </Box>
            )}

            {forumStore.saveError && (
              <Alert severity="error">{forumStore.saveError}</Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/assistant/forum/${sectionSlug}`)}
                disabled={forumStore.saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!title.trim() || !body.trim() || forumStore.saving}
              >
                {forumStore.saving ? (
                  <CircularProgress size={20} />
                ) : (
                  'Create Post'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});
